import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import { Alert, AlertDescription, FormDialog, RadioCard } from '@rentflowdev/ui'
import { useUpdateCurrencyMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import { CURRENCIES } from '@/types'
import type { CurrencyCode } from '@/types'

interface CurrencyDialogProps {
	onOpenChange: (open: boolean) => void
	currency:     CurrencyCode
}

/**
 * Mounted by the caller only while open, so the choice starts from the saved
 * value on every open — same reason `TaxRegimeDialog` takes no `open` prop.
 */
export function CurrencyDialog({ onOpenChange, currency }: CurrencyDialogProps) {
	const { t } = useTranslation()
	const [updateCurrency, { isLoading }] = useUpdateCurrencyMutation()

	const [selected, setSelected] = useState<CurrencyCode>(currency)
	const [error, setError] = useState<string | null>(null)

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()
		setError(null)
		try {
			await updateCurrency({ currency: selected }).unwrap()
			onOpenChange(false)
		} catch (err) {
			setError(extractErrorMessage(err) ?? t('settings.profile.currency.saveFailed'))
		}
	}

	return (
		<FormDialog
			open
			onOpenChange={onOpenChange}
			title={t('settings.profile.currency.dialogTitle')}
			submitLabel={t('common.save')}
			cancelLabel={t('common.cancel')}
			isSubmitting={isLoading}
			onSubmit={handleSubmit}
		>
			<div className="flex flex-col gap-4">
				<p className="text-sm text-(--color-muted-foreground)">
					{t('settings.profile.currency.dialogHint')}
				</p>

				<div className="flex flex-col gap-3">
					{CURRENCIES.map((c) => (
						<RadioCard
							key={c}
							title={t(`currencies.${c}`)}
							description={t(`settings.profile.currency.hints.${c}`)}
							selected={selected === c}
							onClick={() => { setSelected(c); setError(null) }}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									setSelected(c)
									setError(null)
								}
							}}
						/>
					))}
				</div>

				{/* Saying this up front is cheaper than explaining afterwards why a
				    signed contract still shows the old currency. */}
				<p className="text-xs text-(--color-muted-foreground)">
					{t('settings.profile.currency.appliesToNew')}
				</p>

				{error && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}
			</div>
		</FormDialog>
	)
}
